import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

const CustomDropdown = ({ 
  trigger,
  items = [],
  onItemClick,
  position = 'bottom-left', // 'bottom-left', 'bottom-right', 'top-left', 'top-right'
  className = '',
  dropdownClassName = '',
  itemClassName = '',
  closeOnItemClick = true,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle escape key to close dropdown
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleItemClick = (item, index) => {
    if (onItemClick) {
      onItemClick(item, index);
    }
    if (closeOnItemClick) {
      setIsOpen(false);
    }
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  // Determine dropdown position classes
  const getPositionClasses = () => {
    switch (position) {
      case 'bottom-right':
        return 'top-full right-0 mt-1';
      case 'top-left':
        return 'bottom-full left-0 mb-1';
      case 'top-right':
        return 'bottom-full right-0 mb-1';
      case 'bottom-left':
      default:
        return 'top-full left-0 mt-1';
    }
  };

  // Default trigger button if no custom trigger provided
  const defaultTrigger = (
    <button 
      className={`px-3 py-2 text-sm rounded-lg bg-ag-black-800 border border-ag-black-400 
                  hover:bg-ag-green transition-colors flex items-center space-x-2
                  ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      onClick={toggleDropdown}
      disabled={disabled}
    >
      <span>Options</span>
      <ChevronDown 
        size={16} 
        className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
  );

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Trigger Element */}
      {trigger ? (
        <div onClick={toggleDropdown} className={disabled ? 'opacity-50 cursor-not-allowed' : ''}>
          {trigger}
        </div>
      ) : (
        defaultTrigger
      )}

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className={`absolute ${getPositionClasses()} z-50 min-w-[160px] 
                     bg-ag-black-800 border border-ag-black-400 rounded-lg 
                     shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-1
                     ${dropdownClassName}`}
        >
          {items.length > 0 ? (
            <ul className="py-1">
              {items.map((item, index) => {
                // Handle different item types
                if (item === 'divider' || item.type === 'divider') {
                  return (
                    <li key={index} className="border-t border-ag-black-400 my-1" />
                  );
                }

                // Handle disabled items
                const isItemDisabled = item.disabled || false;
                const itemLabel = typeof item === 'string' ? item : (item.label || item.name);
                const itemIcon = typeof item === 'object' ? item.icon : null;
                const itemAction = typeof item === 'object' ? item.action : null;

                return (
                  <li key={index}>
                    <button
                      className={`w-full px-2 py-2 text-sm text-left text-white
                                hover:bg-ag-black-600 transition-colors flex items-center space-x-2
                                ${isItemDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:text-ag-green'}
                                ${itemClassName}`}
                      onClick={() => !isItemDisabled && handleItemClick(itemAction || item, index)}
                      disabled={isItemDisabled}
                    >
                      {itemIcon && <span className="flex-shrink-0">{itemIcon}</span>}
                      <span>{itemLabel}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="px-4 py-2 text-sm text-gray-500">
              No options available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;